import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { DeletionRequest, DeletionRequestsResponse, DeletionRequestStatus } from "@mubin/contracts";
import { displayPhone } from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, Tabs, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { deletionStatusLabel } from "../lib/labels";
import { useMe } from "../lib/session";

/**
 * Account deletion requests (store requirement). The organization's administration completes a request,
 * which removes the person's personal data for good and signs them out everywhere (P-1), or rejects it with a
 * reason.
 */
export function DeletionRequestsPage() {
  const { data: me } = useMe();
  const [status, setStatus] = useState<DeletionRequestStatus>("pending");
  const [deciding, setDeciding] = useState<{
    request: DeletionRequest;
    decision: "complete" | "reject";
  } | null>(null);
  const requests = useQuery({
    queryKey: ["deletion-requests", me?.activeRole, status],
    queryFn: () => api<DeletionRequestsResponse>(`/v1/deletion-requests?status=${status}`),
  });

  return (
    <>
      <PageTitle
        title="طلبات حذف الحساب"
        sub="تصل من التطبيق أو من صفحة الطلب العامة. إتمام الطلب يعطّل الحساب ويخرجه من كل الأجهزة، ويبقى السجل التعليمي محفوظًا حتى تعتمد الجمعية سياسة الحذف."
      />
      <section className="card">
        <div className="toolbar">
          <Tabs
            label="حالة الطلب"
            value={status}
            onChange={setStatus}
            items={(["pending", "completed", "rejected"] as const).map((s) => ({
              value: s,
              label: deletionStatusLabel[s],
            }))}
          />
        </div>
        {requests.isPending ? (
          <Loading />
        ) : requests.isError ? (
          <Empty title="تعذر التحميل">{errorText(requests.error)}</Empty>
        ) : requests.data.deletionRequests.length === 0 ? (
          <Empty title={status === "pending" ? "لا توجد طلبات بانتظار القرار" : "لا توجد طلبات"} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>صاحب الحساب</th>
                  <th>المصدر</th>
                  <th>تاريخ الطلب</th>
                  <th>{status === "pending" ? "" : "القرار"}</th>
                </tr>
              </thead>
              <tbody>
                {requests.data.deletionRequests.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link to="/users/$userId" params={{ userId: r.user.id }}>
                        <b>{r.user.name}</b>
                      </Link>
                      <div className="muted small ltr">{displayPhone(r.user.phone)}</div>
                      {r.note && <div className="muted small">{r.note}</div>}
                    </td>
                    <td>
                      {r.source === "app" ? (
                        "من التطبيق"
                      ) : (
                        <>
                          صفحة الويب
                          <div className="muted small">تحقّق أن الطلب من صاحب الحساب قبل إتمامه</div>
                        </>
                      )}
                    </td>
                    <td className="num small">{r.createdAt.slice(0, 10)}</td>
                    <td className="actions">
                      {r.status === "pending" ? (
                        <div className="row-actions">
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setDeciding({ request: r, decision: "complete" })}
                          >
                            حذف البيانات
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setDeciding({ request: r, decision: "reject" })}
                          >
                            رفض
                          </button>
                        </div>
                      ) : (
                        <>
                          <Badge tone={r.status === "completed" ? "success" : "neutral"}>
                            {deletionStatusLabel[r.status]}
                          </Badge>
                          {r.decisionNote && <div className="muted small">{r.decisionNote}</div>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <DecideModal value={deciding} onClose={() => setDeciding(null)} />
    </>
  );
}

function DecideModal({
  value,
  onClose,
}: {
  value: { request: DeletionRequest; decision: "complete" | "reject" } | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [note, setNote] = useState("");
  const complete = value?.decision === "complete";
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!value) return;
    void run(async () => {
      await send("POST", `/v1/deletion-requests/${value.request.id}/decide`, {
        decision: value.decision,
        note: note.trim() || undefined,
      });
      await qc.invalidateQueries({ queryKey: ["deletion-requests"] });
      await qc.invalidateQueries({ queryKey: ["overview"] });
      setNote("");
      onClose();
    }, errorText);
  }
  return (
    <Modal
      title={
        complete
          ? `حذف بيانات ${value?.request.user.name ?? ""}`
          : `رفض طلب ${value?.request.user.name ?? ""}`
      }
      open={value !== null}
      onClose={onClose}
    >
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        {complete && (
          <p>
            تُحذف بيانات التعريف نهائيًا (الاسم، والجوال، واسم المستخدم، وكلمة المرور)، ويخرج من كل الأجهزة
            فورًا، ويُنهى قيده في حلقاته. تبقى نتائجه وحضوره في سجلات الجمعية باسم «مستخدم محذوف» حتى تبقى
            التقارير صحيحة. <b>لا يمكن التراجع عن هذا.</b>
          </p>
        )}
        <Field
          id="d-note"
          label={complete ? "ملاحظة داخلية (اختياري)" : "سبب الرفض"}
          hint="لا تُرسل لصاحب الحساب"
        >
          <textarea id="d-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
        </Field>
        <div className="form-actions">
          <button className="btn btn-danger" disabled={busy || (!complete && note.trim().length < 3)}>
            {busy ? "جارٍ الحفظ…" : complete ? "حذف البيانات وإتمام الطلب" : "رفض الطلب"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
